import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Input, Select } from '@/components/ui/Field';
import { Banner, ErrorState, Skeleton } from '@/components/ui/Feedback';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { PageHeader } from '@/app/PlaceholderPage';
import { useAuth } from '@/features/auth/AuthContext';
import { databaseApi } from '@/features/database/api';
import { ApiError } from '@/lib/api';
import { cn } from '@/lib/cn';
import { ROLE_LABEL, settingsApi, type AppUser, type Role, type UserInput } from './api';

/** L-15 — Kelola pengguna dan penugasan assessor → TBM. */
export function UsersPage() {
  const { user: me } = useAuth();
  const queryClient = useQueryClient();

  const [editing, setEditing] = useState<AppUser | null | undefined>(undefined);
  const [assigning, setAssigning] = useState<AppUser | null>(null);
  const [deleting, setDeleting] = useState<AppUser | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);

  const { data, isLoading, error: loadError, refetch } = useQuery({
    queryKey: ['users'],
    queryFn: () => settingsApi.listUsers(),
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['users'] });
  const fail = (err: unknown) =>
    setError(err instanceof ApiError ? err.message : 'Gagal menyimpan perubahan.');

  const save = useMutation({
    mutationFn: (input: UserInput) =>
      editing ? settingsApi.updateUser(editing.id, input) : settingsApi.createUser(input),
    onSuccess: () => {
      refresh();
      setEditing(undefined);
      setError(null);
      setBanner('Pengguna tersimpan.');
    },
    onError: fail,
  });

  const toggleActive = useMutation({
    mutationFn: (u: AppUser) => settingsApi.updateUser(u.id, { isActive: !u.isActive }),
    onSuccess: () => {
      refresh();
      setBanner('Status pengguna diperbarui.');
    },
    onError: fail,
  });

  const remove = useMutation({
    mutationFn: () => settingsApi.deleteUser(deleting!.id),
    onSuccess: () => {
      refresh();
      setDeleting(null);
      setBanner('Pengguna dihapus.');
    },
    onError: (err) => {
      setDeleting(null);
      fail(err);
    },
  });

  const saveAssignments = useMutation({
    mutationFn: (tbmIds: string[]) => settingsApi.setAssignments(assigning!.id, tbmIds),
    onSuccess: () => {
      refresh();
      setAssigning(null);
      setError(null);
      setBanner('Penugasan tersimpan.');
    },
    onError: fail,
  });

  const syncPelitaMutation = useMutation({
    mutationFn: () => settingsApi.syncPelitaAccounts(),
    onSuccess: (res) => {
      refresh();
      setBanner(`Berhasil menyinkronkan akun PELITA. Dibuat: ${res.created}, Sudah ada: ${res.skipped}. Total: ${res.total}`);
    },
    onError: fail,
  });

  const resetPasswordMutation = useMutation({
    mutationFn: (u: AppUser) => settingsApi.resetPassword(u.id),
    onSuccess: (res) => {
      setBanner(`Kata sandi akun ${res.username} berhasil direset ke "SLI2026".`);
    },
    onError: fail,
  });

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Pengguna & Penugasan"
        description="Akun yang bisa masuk ke sistem, perannya, dan TBM mana yang boleh mereka lihat."
        action={
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              loading={syncPelitaMutation.isPending}
              onClick={() => syncPelitaMutation.mutate()}
              title="Otomatis buatkan akun login untuk semua individu PELITA yang belum memiliki akun"
            >
              🔄 Sinkronkan Akun PELITA
            </Button>
            <Button onClick={() => setEditing(null)}>+ Pengguna Baru</Button>
          </div>
        }
      />

      {banner && <Banner tone="success">{banner}</Banner>}
      {error && editing === undefined && !assigning && <Banner tone="danger">{error}</Banner>}

      {loadError ? (
        <ErrorState message={(loadError as Error).message} onRetry={() => refetch()} />
      ) : isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : (
        <Card padded={false} className="min-w-0">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] border-collapse text-left">
              <thead>
                <tr className="border-b border-border">
                  <Th>Nama</Th>
                  <Th>Username</Th>
                  <Th>Email</Th>
                  <Th>Peran</Th>
                  <Th>Penugasan</Th>
                  <Th>Status</Th>
                  <Th className="w-44">Aksi</Th>
                </tr>
              </thead>
              <tbody>
                {(data ?? []).map((u) => (
                  <tr key={u.id} className="border-b border-border align-top last:border-0">
                    <Td>
                      <span className="font-medium text-ink">{u.name}</span>
                      {u.id === me?.id && (
                        <span className="ml-2 rounded-full bg-primary-soft px-2 py-0.5 text-[12px] font-medium text-primary">
                          Anda
                        </span>
                      )}
                    </Td>
                    <Td>
                      <span className="tabular text-ink-secondary">{u.username}</span>
                    </Td>
                    <Td>
                      <span className="text-[13px] text-ink-secondary">{u.email}</span>
                    </Td>
                    <Td>{ROLE_LABEL[u.role]}</Td>
                    <Td>
                      {u.role === 'super_admin' ? (
                        <span className="text-[13px] text-ink-muted">seluruh TBM</span>
                      ) : u.assignments.length === 0 ? (
                        <span className="rounded-full bg-warn-soft px-2 py-0.5 text-[12px] font-medium text-[#9a6400]">
                          belum ditugaskan
                        </span>
                      ) : (
                        <span className="text-[13px] text-ink">
                          {u.assignments.map((a) => a.code).join(', ')}
                        </span>
                      )}
                    </Td>
                    <Td>
                      <StatusBadge status={u.isActive ? 'active' : 'inactive'} />
                    </Td>
                    <Td>
                      <div className="flex flex-col gap-1">
                        <Button size="sm" variant="ghost" onClick={() => setEditing(u)}>
                          Ubah
                        </Button>
                        {u.role !== 'super_admin' && (
                          <Button size="sm" variant="ghost" onClick={() => setAssigning(u)}>
                            Penugasan
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => resetPasswordMutation.mutate(u)}
                          disabled={resetPasswordMutation.isPending}
                          title="Reset kata sandi pengguna ini ke SLI2026"
                        >
                          Reset Sandi
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => toggleActive.mutate(u)}
                          disabled={toggleActive.isPending}
                        >
                          {u.isActive ? 'Nonaktifkan' : 'Aktifkan'}
                        </Button>
                        {u.id !== me?.id && (
                          <Button size="sm" variant="ghost" onClick={() => setDeleting(u)}>
                            Hapus
                          </Button>
                        )}
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Card>
        <p className="text-[13px] text-ink-secondary">
          <strong>Super Admin</strong> melihat seluruh TBM tanpa perlu ditugaskan.{' '}
          <strong>Assessor</strong> dan <strong>Pengelola TBM</strong> hanya melihat TBM yang
          ditugaskan padanya — termasuk data PELITA SLI di dalamnya. Yang belum ditugaskan tidak akan
          melihat apa pun, jadi penandanya sengaja dibuat mencolok.
        </p>
      </Card>

      {editing !== undefined && (
        <UserEditor
          user={editing}
          isSelf={editing?.id === me?.id}
          saving={save.isPending}
          error={error}
          onCancel={() => {
            setEditing(undefined);
            setError(null);
          }}
          onSave={(input) => save.mutate(input)}
        />
      )}

      {assigning && (
        <AssignmentEditor
          user={assigning}
          saving={saveAssignments.isPending}
          error={error}
          onCancel={() => {
            setAssigning(null);
            setError(null);
          }}
          onSave={(ids) => saveAssignments.mutate(ids)}
        />
      )}

      <ConfirmDialog
        open={deleting !== null}
        title="Hapus pengguna?"
        confirmLabel="Ya, hapus"
        loading={remove.isPending}
        onCancel={() => setDeleting(null)}
        onConfirm={() => remove.mutate()}
      >
        <p className="mb-3 rounded-[var(--radius-input)] bg-app px-3.5 py-2.5 font-medium text-ink">
          {deleting?.name} · {deleting?.username}
        </p>
        <p>
          Akun ini tidak akan bisa masuk lagi dan seluruh penugasannya ikut hilang. Kalau orangnya
          hanya berhenti aktif, pakai <em>Nonaktifkan</em> — datanya tetap tersimpan.
        </p>
      </ConfirmDialog>
    </div>
  );
}

function UserEditor({
  user,
  isSelf,
  saving,
  error,
  onCancel,
  onSave,
}: {
  user: AppUser | null;
  isSelf: boolean;
  saving: boolean;
  error: string | null;
  onCancel: () => void;
  onSave: (input: UserInput) => void;
}) {
  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [role, setRole] = useState<Role>('assessor');
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    setName(user?.name ?? '');
    setUsername(user?.username ?? '');
    setEmail(user?.email ?? '');
    setPhone(user?.phone ?? '');
    setRole(user?.role ?? 'assessor');
    setPassword('');
    setFieldErrors({});
  }, [user]);

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!name.trim()) errors.name = 'Nama wajib diisi';
    if (!username.trim() || username.trim().length < 3) errors.username = 'Username minimal 3 karakter';
    if (!email.trim() || !email.includes('@')) errors.email = 'Format email tidak valid';
    if (!user && password.length < 8) errors.password = 'Kata sandi minimal 8 karakter';
    if (user && password && password.length < 8) errors.password = 'Kata sandi minimal 8 karakter';

    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      return;
    }

    onSave({
      name: name.trim(),
      username: username.trim(),
      email: email.trim(),
      phone: phone.trim() || null,
      role,
      ...(password ? { password } : {}),
    });
  }

  return (
    <form
      onSubmit={submit}
      noValidate
      className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 sm:items-center sm:p-4"
    >
      <div className="flex max-h-[92vh] w-full max-w-xl flex-col rounded-t-[var(--radius-card)] bg-surface shadow-[var(--shadow-hover)] sm:rounded-[var(--radius-card)]">
        <div className="border-b border-border px-5 py-4">
          <h2 className="text-[19px] font-semibold text-ink">
            {user ? `Ubah ${user.name}` : 'Pengguna Baru'}
          </h2>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5">
          {error && (
            <div className="mb-4">
              <Banner tone="danger">{error}</Banner>
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label="Nama lengkap"
              value={name}
              onChange={(e) => setName(e.target.value)}
              error={fieldErrors.name}
              required
            />
            <Input
              label="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              error={fieldErrors.username}
              required
            />
            <Input
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={fieldErrors.email}
              required
            />
            <Input
              label="No. HP"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>

          <div className="mt-4 sm:max-w-xs">
            <Select
              label="Peran"
              value={role}
              onChange={(e) => setRole(e.target.value as Role)}
              hint={isSelf ? 'Hati-hati mengubah peran akun Anda sendiri.' : undefined}
            >
              <option value="super_admin">Super Admin</option>
              <option value="assessor">Assessor / Monev</option>
              <option value="tbm_manager">Pengelola TBM</option>
            </Select>
          </div>

          <div className="mt-4">
            <Input
              label={user ? 'Kata sandi baru' : 'Kata sandi'}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={fieldErrors.password}
              hint={
                user
                  ? 'Kosongkan bila sandinya tidak diganti.'
                  : 'Minimal 8 karakter. Sampaikan ke pengguna lewat jalur pribadi.'
              }
              required={!user}
            />
          </div>
        </div>

        <div className="flex flex-col-reverse gap-2.5 border-t border-border px-5 py-4 sm:flex-row sm:justify-end">
          <Button type="button" variant="secondary" onClick={onCancel} disabled={saving}>
            Batal
          </Button>
          <Button type="submit" loading={saving}>
            Simpan
          </Button>
        </div>
      </div>
    </form>
  );
}

function AssignmentEditor({
  user,
  saving,
  error,
  onCancel,
  onSave,
}: {
  user: AppUser;
  saving: boolean;
  error: string | null;
  onCancel: () => void;
  onSave: (tbmIds: string[]) => void;
}) {
  const [selected, setSelected] = useState<Set<string>>(
    new Set(user.assignments.map((a) => a.tbmId)),
  );

  const { data, isLoading } = useQuery({
    queryKey: ['tbm', 'all-for-assignment'],
    queryFn: () => databaseApi.listTbm({ page: 1 }),
  });

  const list = data?.data ?? [];
  const allSelected = list.length > 0 && list.every((t) => selected.has(t.id));

  const toggle = (id: string) =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 sm:items-center sm:p-4">
      <div className="flex max-h-[92vh] w-full max-w-lg flex-col rounded-t-[var(--radius-card)] bg-surface shadow-[var(--shadow-hover)] sm:rounded-[var(--radius-card)]">
        <div className="border-b border-border px-5 py-4">
          <h2 className="text-[19px] font-semibold text-ink">Penugasan TBM</h2>
          <p className="mt-0.5 text-[13px] text-ink-secondary">
            {user.name} · {ROLE_LABEL[user.role]}
          </p>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {error && (
            <div className="mb-4">
              <Banner tone="danger">{error}</Banner>
            </div>
          )}

          {isLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : list.length === 0 ? (
            <p className="text-ink-secondary">Belum ada TBM yang bisa ditugaskan.</p>
          ) : (
            <>
              <button
                onClick={() =>
                  setSelected(allSelected ? new Set() : new Set(list.map((t) => t.id)))
                }
                className="mb-2 text-sm font-medium text-primary"
              >
                {allSelected ? 'Kosongkan semua' : 'Pilih semua'}
              </button>

              <ul className="flex flex-col gap-1.5">
                {list.map((t) => (
                  <li key={t.id}>
                    <label
                      className={cn(
                        'flex min-h-11 cursor-pointer items-center gap-3 rounded-[var(--radius-input)] border px-3.5 py-2',
                        selected.has(t.id)
                          ? 'border-primary bg-primary-soft/40'
                          : 'border-border',
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={selected.has(t.id)}
                        onChange={() => toggle(t.id)}
                        className="size-4 accent-[color:var(--color-primary)]"
                      />
                      <span className="min-w-0">
                        <span className="tabular text-[13px] text-ink-secondary">{t.code}</span>{' '}
                        <span className="text-ink">{t.name}</span>
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        <div className="flex flex-col-reverse gap-2.5 border-t border-border px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-[13px] text-ink-secondary">{selected.size} TBM dipilih</p>
          <div className="flex flex-col-reverse gap-2.5 sm:flex-row">
            <Button type="button" variant="secondary" onClick={onCancel} disabled={saving}>
              Batal
            </Button>
            <Button onClick={() => onSave([...selected])} loading={saving}>
              Simpan Penugasan
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th
      scope="col"
      className={cn('px-4 py-3 text-[13px] font-semibold text-ink-secondary', className)}
    >
      {children}
    </th>
  );
}

function Td({ children }: { children: React.ReactNode }) {
  return <td className="px-4 py-3.5 align-top">{children}</td>;
}
